import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { mentorsAPI, recommendationsAPI } from '../services/api';
import { MentorCard } from '../components/Card';
import Modal from '../components/Modal';
import './ListPage.css';

const DOMAINS = ['Tech', 'Finance', 'Marketing', 'RH', 'Droit', 'Santé', 'Éducation', 'Commerce', 'Industrie'];

export default function Mentors() {
  const { user } = useAuth();

  // ── Recommandations ────────────────────────────────────────────────
  const [recommended, setRecommended]   = useState([]);
  const [recMeta, setRecMeta]           = useState(null);
  const [loadingRec, setLoadingRec]     = useState(false);

  // ── Liste complète ─────────────────────────────────────────────────
  const [mentors, setMentors]   = useState([]);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [domain, setDomain]     = useState('');
  const [page, setPage]         = useState(1);

  // ── Contact modal ──────────────────────────────────────────────────
  const [contactMentor, setContactMentor] = useState(null);
  const [form, setForm]     = useState({ topic: '', message: '' });
  const [sending, setSending] = useState(false);
  const [toast, setToast]   = useState('');

  // Charge les recommandations une seule fois à la connexion
  useEffect(() => {
    if (!user) { setRecommended([]); setRecMeta(null); return; }
    let cancelled = false;
    setLoadingRec(true);
    recommendationsAPI.index({ limit: 3 })
      .then(({ data }) => {
        if (cancelled) return;
        setRecommended(data.data || []);
        setRecMeta(data.meta || null);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingRec(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Charge la liste filtrée
  const fetchMentors = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mentorsAPI.list({ search, domain, page });
      setMentors(data.data);
      setMeta(data.meta);
    } catch {}
    finally { setLoading(false); }
  }, [search, domain, page]);

  useEffect(() => { fetchMentors(); }, [fetchMentors]);

  const handleContact = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = '/login'; return; }
    setSending(true);
    try {
      await mentorsAPI.contact(contactMentor.id, form);
      setToast('Demande envoyée ! Le mentor vous contactera sous 48h.');
      setContactMentor(null);
      setForm({ topic: '', message: '' });
    } catch (err) {
      setToast(err.response?.data?.message || 'Erreur lors de l\'envoi.');
    } finally { setSending(false); }
  };

  // Libellé de la méthode de recommandation (affiché à l'utilisateur)
  const recMethodLabel = recMeta
    ? (recMeta.based_on === 'domain_ais_wilaya'
        ? `Domaine "${recMeta.user_domain}", wilaya et score AIS`
        : 'Score AIS — complétez votre profil pour affiner')
    : '';

  // Libellé du moteur de recommandation, dérivé de meta.engine (évite tout codage en dur)
  const recEngineLabel = recMeta?.engine === 'hybrid-rule-nlp-v2'
    ? 'Matching hybride v2'
    : 'Moteur de recommandation';

  return (
    <div className="list-page">
      <div className="page-header">
        <h1>Mentors</h1>
        <p>Les membres actifs qui font circuler savoir et opportunités dans l'écosystème.</p>
      </div>

      {/* ── Section Recommandations ── visible uniquement si connecté ── */}
      {user && (
        <div className="rec-section">
          <div className="rec-header">
            <h2>Recommandés pour vous</h2>
            <span className="rec-badge">{recEngineLabel}</span>
            {recMeta && (
              <span className="rec-hint">Basé sur : {recMethodLabel}</span>
            )}
          </div>

          {loadingRec ? (
            <div className="loading" style={{ padding: '1.5rem 0' }}>Calcul en cours…</div>
          ) : recommended.length === 0 ? (
            <p className="rec-empty">Aucune recommandation disponible pour l'instant.</p>
          ) : (
            <div className="grid-3">
              {recommended.map((m) => (
                <MentorCard key={m.id} mentor={m} onContact={setContactMentor} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Filtres + liste complète ───────────────────────────────── */}
      <div className="filters">
        <input
          type="text"
          placeholder="Rechercher un mentor..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="search-input"
        />
        <select value={domain} onChange={(e) => { setDomain(e.target.value); setPage(1); }} className="filter-select">
          <option value="">Tous les domaines</option>
          {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : mentors.length === 0 ? (
        <div className="empty">Aucun mentor trouvé.</div>
      ) : (
        <div className="grid-3">
          {mentors.map((m) => (
            <MentorCard key={m.id} mentor={m} onContact={setContactMentor} />
          ))}
        </div>
      )}

      {meta.last_page > 1 && (
        <div className="pagination">
          {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)} className={`page-btn ${p === page ? 'active' : ''}`}>{p}</button>
          ))}
        </div>
      )}

      {toast && <div className="toast" onClick={() => setToast('')}>{toast}</div>}

      {contactMentor && (
        <Modal title={`Contacter ${contactMentor.user?.first_name}`} onClose={() => setContactMentor(null)}>
          <form onSubmit={handleContact} className="modal-form">
            <label>Sujet *</label>
            <input
              required maxLength={200}
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="Ex : Reconversion vers le digital"
            />
            <label>Message *</label>
            <textarea
              required maxLength={2000} rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Présentez votre situation et votre demande..."
            />
            <button type="submit" className="btn-submit" disabled={sending}>
              {sending ? 'Envoi...' : 'Envoyer la demande'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
